/**
 * Step 1 of the Obsidian import: read the vault and describe what would be
 * imported, without touching the store. The wizard shows this as a report.
 */
import { parseMarkdown, splitFrontmatter } from '@nexus/markdown';
import type { JSONContent } from '@tiptap/core';

export interface VaultFile {
  /** Path relative to the vault root, with forward slashes. */
  path: string;
  size: number;
  read(): Promise<Uint8Array>;
}

export interface NoteInfo {
  path: string;
  folder: string;
  title: string;
  text: string;
  frontmatterKeys: string[];
  invalidFrontmatter: boolean;
  isConflict: boolean;
}

export interface KeyStat {
  key: string;
  count: number;
  /** Proposed Nexus property name; null = do not import. */
  suggested: string | null;
}

export interface VaultAnalysis {
  notes: NoteInfo[];
  attachments: VaultFile[];
  skipped: { path: string; reason: 'hidden' | 'system' }[];
  conflicts: string[];
  keys: KeyStat[];
  folders: string[];
  dailyFolder: string | null;
  templatesFolder: string | null;
  /** Folder whose notes are tasks (status + due date properties), Notion-style. */
  tasksFolder: string | null;
  sensitiveFolders: string[];
  stats: {
    wikilinks: number;
    embeds: number;
    tasks: number;
    dataviewBlocks: number;
    tasksQueryBlocks: number;
    rawBlocks: number;
    invalidFrontmatter: number;
  };
}

const MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  pdf: 'application/pdf',
  csv: 'text/csv',
  txt: 'text/plain',
  json: 'application/json',
  mp3: 'audio/mpeg',
  mp4: 'video/mp4',
  webm: 'video/webm',
  canvas: 'application/json',
};

export const mimeOf = (path: string) =>
  MIME[path.split('.').pop()?.toLowerCase() ?? ''] ?? 'application/octet-stream';

/** Obsidian/Notion-ish synonyms folded onto one Nexus property name. */
const SYNONYMS: Record<string, string> = {
  status: 'status',
  state: 'status',
  priority: 'priority',
  prioriteit: 'priority',
  prio: 'priority',
  due: 'due',
  'due date': 'due',
  duedate: 'due',
  deadline: 'due',
  type: 'type',
  category: 'category',
  categorie: 'category',
  owner: 'owner',
  eigenaar: 'owner',
  description: 'description',
  beschrijving: 'description',
  omschrijving: 'description',
  tags: 'tags',
  tag: 'tags',
  aliases: 'aliases',
  alias: 'aliases',
  project: 'project',
  cssclasses: 'cssclasses',
};

export function suggestKey(key: string, allKeys: Set<string>): string {
  const lower = key.toLowerCase().trim();
  if (SYNONYMS[lower]) return SYNONYMS[lower];
  // `Type` next to `type`: fold case-only variants together.
  const lowerExists = [...allKeys].some((k) => k !== key && k.toLowerCase() === lower);
  return lowerExists ? lower : key;
}

const SENSITIVE =
  /(^|[\s/_-])(hr|sollicitatie|sollicitaties|evaluatie\w*|loon\w*|salaris\w*|medisch|priv[eé])([\s/_-]|$)/i;
const DATE_NAME = /^\d{4}-\d{2}-\d{2}$/;
const CONFLICT = /\.sync-conflict-\d{8}-\d{6}-[A-Z0-9]+$|\(conflicted copy[^)]*\)$/;

const decoder = new TextDecoder();

function countNodes(doc: JSONContent, pred: (n: JSONContent) => boolean): number {
  let n = pred(doc) ? 1 : 0;
  for (const c of doc.content ?? []) n += countNodes(c, pred);
  return n;
}

export async function analyzeVault(files: VaultFile[]): Promise<VaultAnalysis> {
  const notes: NoteInfo[] = [];
  const attachments: VaultFile[] = [];
  const skipped: VaultAnalysis['skipped'] = [];
  const keyCounts = new Map<string, number>();
  const stats: VaultAnalysis['stats'] = {
    wikilinks: 0,
    embeds: 0,
    tasks: 0,
    dataviewBlocks: 0,
    tasksQueryBlocks: 0,
    rawBlocks: 0,
    invalidFrontmatter: 0,
  };
  let dailyFromConfig: string | null = null;
  let templatesFromConfig: string | null = null;
  let newNotesFolder: string | null = null;

  for (const file of files) {
    const parts = file.path.split('/');
    const name = parts[parts.length - 1]!;
    if (file.path === '.obsidian/app.json') {
      try {
        const cfg = JSON.parse(decoder.decode(await file.read())) as { newFileFolderPath?: string };
        newNotesFolder = cfg.newFileFolderPath ?? null;
      } catch {
        /* ignore unreadable config */
      }
    }
    if (file.path === '.obsidian/daily-notes.json' || file.path === '.obsidian/templates.json') {
      try {
        const cfg = JSON.parse(decoder.decode(await file.read())) as { folder?: string };
        if (cfg.folder && file.path.endsWith('daily-notes.json')) dailyFromConfig = cfg.folder;
        if (cfg.folder && file.path.endsWith('templates.json')) templatesFromConfig = cfg.folder;
      } catch {
        /* ignore unreadable config */
      }
    }
    if (parts.some((p) => p.startsWith('.'))) {
      skipped.push({ path: file.path, reason: 'hidden' });
      continue;
    }
    if (/^(desktop\.ini|thumbs\.db)$/i.test(name)) {
      skipped.push({ path: file.path, reason: 'system' });
      continue;
    }
    if (!name.toLowerCase().endsWith('.md')) {
      attachments.push(file);
      continue;
    }
    const text = decoder.decode(await file.read());
    const title = name.slice(0, -3);
    const folder = parts.slice(0, -1).join('/');
    const fm = splitFrontmatter(text);
    const keys = Object.keys(fm.frontmatter ?? {});
    keys.forEach((k) => keyCounts.set(k, (keyCounts.get(k) ?? 0) + 1));
    if (fm.invalidFrontmatter !== null) stats.invalidFrontmatter++;
    const doc = parseMarkdown(text).doc;
    stats.wikilinks += countNodes(doc, (n) => n.type === 'wikiLink');
    stats.embeds += countNodes(doc, (n) => n.type === 'embed');
    stats.tasks += countNodes(doc, (n) => n.type === 'taskItem');
    stats.rawBlocks += countNodes(doc, (n) => n.type === 'rawBlock');
    stats.dataviewBlocks += countNodes(
      doc,
      (n) => n.type === 'codeBlock' && n.attrs?.language === 'dataview',
    );
    stats.tasksQueryBlocks += countNodes(
      doc,
      (n) => n.type === 'codeBlock' && n.attrs?.language === 'tasks',
    );
    notes.push({
      path: file.path,
      folder,
      title,
      text,
      frontmatterKeys: keys,
      invalidFrontmatter: fm.invalidFrontmatter !== null,
      isConflict: CONFLICT.test(title),
    });
  }

  const allKeys = new Set(keyCounts.keys());
  const keys = [...keyCounts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key, count]) => ({ key, count, suggested: suggestKey(key, allKeys) }));

  const folders = new Set<string>();
  for (const n of notes) {
    const parts = n.folder ? n.folder.split('/') : [];
    for (let i = 1; i <= parts.length; i++) folders.add(parts.slice(0, i).join('/'));
  }
  const folderList = [...folders].sort((a, b) => a.localeCompare(b));

  // Daily notes: the configured folder, else the folder with the most YYYY-MM-DD notes.
  const dailyCounts = new Map<string, number>();
  for (const n of notes)
    if (DATE_NAME.test(n.title)) dailyCounts.set(n.folder, (dailyCounts.get(n.folder) ?? 0) + 1);
  const busiest = [...dailyCounts.entries()].sort((a, b) => b[1] - a[1])[0];
  const dailyFolder =
    dailyFromConfig && folders.has(dailyFromConfig)
      ? dailyFromConfig
      : busiest && busiest[1] >= 3
        ? busiest[0]
        : null;
  const templatesFolder =
    templatesFromConfig && folders.has(templatesFromConfig)
      ? templatesFromConfig
      : (folderList.find((f) => /(^|\/)(templates|sjablonen)$/i.test(f)) ?? null);

  // Tasks folder: most notes carry both a status-like and a due-like property.
  const taskish = new Map<string, { yes: number; all: number; dated: number }>();
  for (const n of notes) {
    const mapped = new Set(n.frontmatterKeys.map((k) => suggestKey(k, allKeys)));
    const c = taskish.get(n.folder) ?? { yes: 0, all: 0, dated: 0 };
    c.all++;
    if (mapped.has('status')) c.yes++;
    if (mapped.has('due') || n.frontmatterKeys.some((k) => /^date$/i.test(k))) c.dated++;
    taskish.set(n.folder, c);
  }
  // Prefer where Obsidian puts new notes, then folders called "Taken"/"Tasks";
  // avoid archives (they hold old, finished tasks).
  const candidates = [...taskish.entries()].filter(
    ([f, c]) => f && c.yes >= 3 && c.yes / c.all >= 0.6 && c.dated > 0,
  );
  const score = ([f, c]: [string, { yes: number }]) =>
    (f === newNotesFolder ? 1000 : 0) +
    (/(^|\/)(taken|tasks|to-?dos?)$/i.test(f) ? 500 : 0) -
    (/(^|\/)(archief|archive)(\/|$)/i.test(f) ? 800 : 0) +
    c.yes;
  const tasksFolder = candidates.sort((a, b) => score(b) - score(a))[0]?.[0] ?? null;

  // Suggest the top-most folders whose name looks personal/HR-related.
  const sensitiveFolders = folderList.filter(
    (f) =>
      SENSITIVE.test(f.split('/').pop()!) &&
      !folderList.some(
        (g) => g !== f && f.startsWith(`${g}/`) && SENSITIVE.test(g.split('/').pop()!),
      ),
  );

  return {
    notes,
    attachments,
    skipped,
    conflicts: notes.filter((n) => n.isConflict).map((n) => n.path),
    keys,
    folders: folderList,
    dailyFolder,
    templatesFolder,
    tasksFolder,
    sensitiveFolders,
    stats,
  };
}
